/**
 * Admin Portal → Partners → a partner → Links & tags → a tag → Email kit (partner launch UC3,
 * workstream S5). docs/plans/PARTNER_LAUNCH_PLAN_2026_10_17.md §1 UC3.
 *
 * What we hand a partner to put in their OWN email: the tag's shared code, what it gives, plain
 * App Store and Google Play links and the redeem steps — as HTML for their email tool and as
 * plain text. Built by `emailKit.ts` (pure, jest-pinned; escaping shared with the website code).
 * The preview is a sandboxed iframe with no permissions, like the website code card's.
 *
 * It reads the tag's ACTIVE shared code; without one it says how to make it. A tracking-only tag
 * is warned about (people rarely type a code that gives them nothing), and a paused tag is warned
 * that its code still grants the offer but attributes nobody. The card hides itself once the tag
 * or its partner has ended. Every admin role sees it (copying is a read).
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { tagEditable, type CodeRow, type PartnerStatus, type TagRow } from './partnersLogic';
import { buildEmailKit } from './emailKit';
import { previewDocument } from './websiteCode';
import { copyText, errText, hint, input, row } from './PartnersShared';

type CardNotice = { kind: 'ok' | 'error'; text: string } | null;

export const PartnerEmailKit: React.FC<{
  tag: TagRow;
  partnerName: string;
  partnerStatus: PartnerStatus;
  /** Bumped when a code is made on this tag, so the kit re-reads it. */
  refreshKey?: number;
}> = ({ tag, partnerName, partnerStatus, refreshKey = 0 }) => {
  const live = tagEditable(tag.status, partnerStatus);
  const [code, setCode] = useState<CodeRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<CardNotice>(null);

  useEffect(() => {
    if (!live) return undefined;
    let on = true;
    setLoadError(null);
    PartnersAdmin.listCodes({ tag_id: tag.id, kind: 'shared', state: 'active', limit: 1 })
      .then((res) => { if (on) setCode(res.codes?.[0] ?? null); })
      .catch((e) => { if (on) { setCode(null); setLoadError(errText(e)); } })
      .finally(() => { if (on) setLoaded(true); });
    return () => { on = false; };
  }, [tag.id, live, refreshKey]);

  const kit = useMemo(
    () => (code ? buildEmailKit({ partnerName, code: code.code, offer: tag, expiresAt: code.expires_at, maxRedemptions: code.max_redemptions }) : null),
    [code, partnerName, tag],
  );

  // An ended tag or partner: nothing to hand over (after the hooks — rules of hooks).
  if (!live) return null;

  const copy = async (what: 'HTML' | 'plain text', value: string) => {
    setNotice((await copyText(value))
      ? { kind: 'ok', text: `Copied the ${what}.` }
      : { kind: 'error', text: 'The browser refused the copy — select the text and copy it by hand.' });
  };

  const paused = tag.status === 'paused' || partnerStatus === 'paused';
  const area: React.CSSProperties = {
    ...input, display: 'block', width: '100%', minHeight: 140, marginTop: 6,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12,
  };

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Email kit</h4>
      <div style={hint}>
        For {partnerName}&apos;s own email: the shared code, what it gives, the App Store and Google Play links and how to
        redeem. No website hop — the person installs, signs up and types the code, and it is credited to {partnerName}.
      </div>
      {loadError && <div style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{loadError}</div>}
      {loaded && !code && !loadError && (
        <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
          This tag has no active shared code yet — make one below (Codes → Shared code) to get the kit.
        </div>
      )}
      {paused && (
        <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
          This {tag.status === 'paused' ? 'tag' : 'partner'} is paused: the code still grants its offer, but redemptions attribute
          nobody while it stays paused.
        </div>
      )}
      {kit && !kit.ok && <div style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{kit.reason}</div>}
      {kit && kit.ok && (
        <>
          {!kit.offer && (
            <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>
              This tag is tracking only: the code gives the person nothing beyond the free week everyone gets, so few will
              type it. Set an offer (Offer and bounties, above) before sending the kit.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginTop: 8 }}>
            <div>
              <b style={{ fontSize: 13 }}>HTML (for their email tool)</b>
              <textarea aria-label="Email kit HTML" readOnly value={kit.html} onFocus={(e) => e.currentTarget.select()} style={area} />
              <div style={{ ...row, marginTop: 6 }}>
                <AdminButton size="sm" onClick={() => copy('HTML', kit.html)}>Copy HTML</AdminButton>
              </div>
            </div>
            <div>
              <b style={{ fontSize: 13 }}>Plain text</b>
              <textarea aria-label="Email kit plain text" readOnly value={kit.text} onFocus={(e) => e.currentTarget.select()} style={area} />
              <div style={{ ...row, marginTop: 6 }}>
                <AdminButton size="sm" variant="secondary" onClick={() => copy('plain text', kit.text)}>Copy plain text</AdminButton>
              </div>
            </div>
          </div>
          {notice?.kind === 'ok' && <div role="status" style={hint}>{notice.text}</div>}
          {notice?.kind === 'error' && <div role="alert" style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{notice.text}</div>}
          <div style={{ ...hint, marginTop: 12 }}>Preview — links are turned off here.</div>
          <iframe
            title={`Email kit preview for ${tag.tag}`}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={previewDocument(kit.html)}
            style={{ width: '100%', height: 240, border: '1px dashed var(--border-strong, #d1d5db)', borderRadius: 8, background: '#ffffff' }}
          />
        </>
      )}
    </AdminCard>
  );
};

export default PartnerEmailKit;
